import { FLAG_ACTIVE, PATH_EASE, PATH_FLAG_SCALE_ACTIVE, PATH_FLAG_SCALE_AFFECT, PATH_FLAG_SYNC_ROTATION } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { ParticleCore, PathSlotLanes, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { FlatPathLookup } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';

/**
 * Path kernel — verbatim port of src/particles/behaviour/ParticlePath.tick()
 * (the parity oracle; do not "improve" the math). Runs once per path slot
 * (orbit, then icon — the reference add-order), each with its own lane set
 * and behaviourMask bit; the shared body lives here.
 *
 * Reference tick order, preserved exactly:
 *  1. _processPosition: advance the path index, ADD the table offset to the
 *     x/y targets (increaseTargetX/Y — this kernel runs after progress/mouse
 *     parallax wrote/added to tx/ty, like the behaviour add-order);
 *  2. _processScale IF _scaleActive;
 *  3. tick the expand + speedMul Inertias — AFTER the position read, so a
 *     frame's offset uses the PREVIOUS frame's eased values.
 *
 * Reference math notes (all kept bug-compatible):
 *  - index advance: idx += dir * speed * speedMul.value * deltaTime; the
 *    wrap is asymmetric — `>= length` snaps to 0, `<= 0` snaps to length-1
 *    (fractional overshoot is discarded, and an exact 0 wraps to the end);
 *  - table read at floor(idx);
 *  - offset = table[i] * (mul * expand.value) * s, where s is
 *    zones.spaces.scale when scaleAffect (the SPACES zone scale even on the
 *    home/icon orbits — reference quirk) — passed in as spacesZoneScale;
 *  - syncRotation (enabled by the icon department's ~12% triangle shapes):
 *    degrees = round((360 / length) * idx), +180 when dir == 1, written
 *    STRAIGHT to the sprite (no ease; the write-if-changed guard in the
 *    reference is a retained-sprite optimisation — value-identical here);
 *  - _processScale advances scaleIdx WITHOUT deltaTime and WITHOUT speedMul
 *    (frame-rate dependent — reference behaviour), same asymmetric wrap,
 *    then targetScale = min + (max - min) * table[floor(scaleIdx)].scale;
 *  - the Inertias are Inertia(5, 1): fixed ease PATH_EASE, plain ease path
 *    value += ((target - value) / ease) * deltaTime.
 *
 * NOT ported (no home caller): hardPositionUpdate (ScreensGridParticle only),
 * the alphaActive flag (its reference body is empty).
 */
export function pathKernel(
	core: ParticleCore,
	lanes: PathSlotLanes,
	range: PoolRange,
	behaviourBit: number,
	lookups: ReadonlyArray<FlatPathLookup>,
	deltaTime: number,
	spacesZoneScale: number
): void {
	const mask = core.behaviourMask;
	const flags = core.stateFlags;
	const degToRad = Math.PI / 180;

	for (let i = range.start; i < range.end; i++) {
		if (!(flags[i] & FLAG_ACTIVE) || !(mask[i] & behaviourBit)) {
			continue;
		}

		const table = lookups[lanes.lookupId[i]];
		const length = table.length;
		const pathFlags = lanes.flags[i];

		//_processPosition — index advance + asymmetric wrap:
		let idx = lanes.idx[i] + lanes.dir[i] * lanes.speed[i] * lanes.speedMul[i] * deltaTime;
		if (idx >= length) {
			idx = 0;
		} else if (idx <= 0) {
			idx = length - 1;
		}
		lanes.idx[i] = idx;

		const rounded = Math.floor(idx);
		const s = pathFlags & PATH_FLAG_SCALE_AFFECT ? spacesZoneScale : 1;
		const mul = lanes.expand[i] * s;

		core.tx[i] += table.x[rounded] * lanes.xMul[i] * mul;
		core.ty[i] += table.y[rounded] * lanes.yMul[i] * mul;

		if (pathFlags & PATH_FLAG_SYNC_ROTATION) {
			let degrees = Math.round((360 / length) * idx);
			if (lanes.dir[i] === 1) {
				degrees += 180;
			}
			core.rotation[i] = degrees * degToRad;
		}

		//_processScale — gated on scaleActive; note: no deltaTime, no speedMul:
		if (pathFlags & PATH_FLAG_SCALE_ACTIVE) {
			let scaleIdx = lanes.scaleIdx[i] + lanes.dir[i] * lanes.speed[i];
			if (scaleIdx >= length) {
				scaleIdx = 0;
			} else if (scaleIdx <= 0) {
				scaleIdx = length - 1;
			}
			lanes.scaleIdx[i] = scaleIdx;

			core.tScale[i] =
				lanes.scaleMin[i] + (lanes.scaleMax[i] - lanes.scaleMin[i]) * table.scale[Math.floor(scaleIdx)];
		}

		//The two Inertia ticks — after the position read (reference order):
		lanes.expand[i] += ((lanes.expandTarget[i] - lanes.expand[i]) / PATH_EASE) * deltaTime;
		lanes.speedMul[i] += ((lanes.speedMulTarget[i] - lanes.speedMul[i]) / PATH_EASE) * deltaTime;
	}
}
